import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { LoadingSpinner } from './LoadingSpinner';
import { validateVerificationToken, verifyEmailAndSetPassword } from '../services/authService';
import './EmailVerification.css';

interface User {
	id: string;
	email: string;
	firstName: string;
	lastName: string;
	emailVerified: boolean;
}

const EmailVerification: React.FC = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const token = searchParams.get('token');

	const [loading, setLoading] = useState(true);
	const [tokenValid, setTokenValid] = useState(false);
	const [user, setUser] = useState<User | null>(null);
	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [error, setError] = useState('');
	const [success, setSuccess] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	const validateToken = useCallback(async () => {
		if (!token) {
			setError('Nedostaje verifikacioni token');
			setLoading(false);
			return;
		}

		try {
			const data = await validateVerificationToken(token);

			if (data.valid) {
				setTokenValid(true);
				setUser(data.user);
			} else {
				setError('Nevažeći ili istekao verifikacioni link');
			}
		} catch (err: any) {
			console.error('Token validation error:', err);
			setError(err.message || 'Greška pri proveri verifikacionog linka');
		} finally {
			setLoading(false);
		}
	}, [token]);

	useEffect(() => {
		validateToken();
	}, [validateToken]);

	const validatePassword = (password: string): string[] => {
		const errors: string[] = [];

		if (password.length < 8) {
			errors.push('Lozinka mora imati minimum 8 karaktera');
		}

		return errors;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		// Clear previous errors
		setError('');

		// Validate password
		const passwordErrors = validatePassword(password);
		if (passwordErrors.length > 0) {
			setError(passwordErrors[0]);
			return;
		}

		// Check if passwords match
		if (password !== confirmPassword) {
			setError('Lozinke se ne poklapaju');
			return;
		}

		setSubmitting(true);

		try {
			const data = await verifyEmailAndSetPassword(token!, password);

			if (data.success) {
				setSuccess(true);
				// Redirect to login after 5 seconds
				setTimeout(() => navigate('/'), 5000);
			} else {
				setError(data.message || 'Greška pri aktivaciji naloga');
			}
		} catch (err: any) {
			console.error('Verification error:', err);
			setError(err.message || 'Greška pri aktivaciji naloga. Molimo pokušajte ponovo.');
		} finally {
			setSubmitting(false);
		}
	};

	const handleGoToLogin = () => {
		navigate('/');
	};

	// Loading state
	if (loading) {
		return (
			<div className='email-verification-container'>
				<div className='verification-card loading-card'>
					<LoadingSpinner />
					<p>Proveravamo vaš verifikacioni link...</p>
				</div>
			</div>
		);
	}

	// Error state (invalid/expired token)
	if (!tokenValid || (error && !user)) {
		return (
			<div className='email-verification-container'>
				<div className='verification-card error-card'>
					<h2>Nevažeći link</h2>
					<p>{error || 'Link za verifikaciju je nevažeći ili je istekao.'}</p>
					<p>Molimo kontaktirajte administratora za novi verifikacioni link.</p>
					<button className='btn btn-primary' onClick={handleGoToLogin}>
						Idi na login
					</button>
				</div>
			</div>
		);
	}

	// Success state
	if (success) {
		return (
			<div className='email-verification-container'>
				<div className='verification-card success-card'>
					<div className='success-icon'>✓</div>
					<h2>Nalog uspešno aktiviran!</h2>
					<p>Vaš email je verifikovan i lozinka je postavljena.</p>
					<p>Sada možete da se prijavite sa vašim podacima.</p>
					<p className='redirect-message'>Preusmjeravamo vas na login stranicu...</p>
					<button className='btn btn-primary' onClick={handleGoToLogin}>
						Prijavite se odmah
					</button>
				</div>
			</div>
		);
	}

	// Main form state
	return (
		<div className='email-verification-container'>
			<div className='verification-card'>
				<h2>Aktivacija naloga</h2>
				<div className='welcome-message'>
					<p>
						Dobrodošli,{' '}
						<strong>
							{user?.firstName} {user?.lastName}
						</strong>
						!
					</p>
					<p>
						Vaš email (<strong>{user?.email}</strong>) je spreman za verifikaciju.
					</p>
					<p>Molimo postavite vašu lozinku da završite aktivaciju naloga.</p>
				</div>

				<form onSubmit={handleSubmit} className='password-form'>
					<div className='form-group'>
						<label htmlFor='password'>Nova lozinka *</label>
						<input type='password' id='password' value={password} onChange={(e) => setPassword(e.target.value)} placeholder='Minimum 8 karaktera' required disabled={submitting} />
					</div>

					<div className='form-group'>
						<label htmlFor='confirmPassword'>Potvrdite lozinku *</label>
						<input type='password' id='confirmPassword' value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder='Ponovite lozinku' required disabled={submitting} />
					</div>

					{error && <div className='error-message'>{error}</div>}

					<button type='submit' disabled={submitting || !password || !confirmPassword} className='btn btn-primary submit-btn'>
						{submitting ? (
							<>
								<LoadingSpinner size='small' />
								Aktiviranje...
							</>
						) : (
							'Aktiviraj nalog'
						)}
					</button>
				</form>
			</div>
		</div>
	);
};

export default EmailVerification;
